import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Users,
  Settings,
  Wallet,
  Zap,
  ArrowLeftRight,
  FileText,
  CreditCard,
  TrendingUp,
  Bell,
  User,
  LogOut,
  ChevronDown,
  Search,
  Menu as MenuIcon,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  children?: MenuItem[];
}

const MENU_ITEMS: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/admin/dashboard' },
  { 
    id: 'banking', 
    label: 'Banking', 
    icon: Wallet, 
    path: '/admin/banking',
    children: [
      { id: 'accounts', label: 'Contas', icon: Wallet, path: '/admin/accounts' },
      { id: 'transactions', label: 'Transações', icon: ArrowLeftRight, path: '/admin/transactions' },
      { id: 'pix', label: 'PIX', icon: Zap, path: '/admin/pix' },
    ]
  },
  { 
    id: 'users', 
    label: 'Usuários', 
    icon: Users, 
    path: '/admin/users',
  },
  { 
    id: 'reports', 
    label: 'Relatórios', 
    icon: FileText, 
    path: '/admin/reports',
  },
  { id: 'settings', label: 'Configurações', icon: Settings, path: '/admin/settings' },
];

function TopbarMenuItem({ item }: { item: MenuItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.path);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Link
        to={hasChildren ? '#' : item.path}
        onClick={(e) => hasChildren && e.preventDefault()}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
          isActive 
            ? 'bg-primary-500/20 text-primary-400' 
            : 'text-surface-300 hover:bg-surface-800 hover:text-white'
        )}
      >
        <item.icon className="w-4 h-4" />
        <span className="text-sm font-medium">{item.label}</span>
        {hasChildren && (
          <ChevronDown className={cn(
            'w-4 h-4 transition-transform',
            isOpen && 'rotate-180'
          )} />
        )}
      </Link>

      {/* Dropdown */}
      <AnimatePresence>
        {hasChildren && isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 mt-1 py-2 bg-surface-800 rounded-lg shadow-xl border border-surface-700 min-w-[200px] z-50"
          >
            {item.children?.map((child) => (
              <Link
                key={child.id}
                to={child.path}
                className="flex items-center gap-3 px-4 py-2 text-surface-300 hover:bg-surface-700 hover:text-white transition-colors"
              >
                <child.icon className="w-4 h-4" />
                <span className="text-sm">{child.label}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function TopbarLayout() {
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <div className="min-h-screen bg-surface-950">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="grid-bg absolute inset-0 opacity-30" />
      </div>

      {/* Topbar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-surface-900/95 backdrop-blur-xl border-b border-surface-800 z-40">
        <div className="h-full max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">U</span>
            </div>
            <span className="text-white font-semibold">UniãoBR</span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {MENU_ITEMS.map((item) => (
              <TopbarMenuItem key={item.id} item={item} />
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Search */}
            <button className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors">
              <Search className="w-5 h-5" />
            </button>

            {/* Notifications */}
            <button className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            {/* User Menu */}
            <div 
              className="relative"
              onMouseEnter={() => setShowUserMenu(true)}
              onMouseLeave={() => setShowUserMenu(false)}
            >
              <button className="flex items-center gap-2 p-2 text-surface-300 hover:text-white hover:bg-surface-800 rounded-lg transition-colors">
                <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-400" />
                </div>
                <span className="text-sm hidden lg:block">{user?.name || 'Usuário'}</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-1 py-2 bg-surface-800 rounded-lg shadow-xl border border-surface-700 min-w-[180px] z-50"
                  >
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-2 text-surface-300 hover:bg-surface-700 hover:text-white transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm">Perfil</span>
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2 text-surface-300 hover:bg-surface-700 hover:text-white transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">Configurações</span>
                    </Link>
                    <hr className="my-2 border-surface-700" />
                    <button
                      onClick={() => logout()}
                      className="flex items-center gap-3 px-4 py-2 text-red-400 hover:bg-surface-700 transition-colors w-full"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">Sair</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-7xl mx-auto p-6"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}

export default TopbarLayout;
